/* object.control_adapter.js */

var $ = require("jquery");
var _ = require("lodash");
var EventEmitter = require("events").EventEmitter;
var util = require("util");
var q = require("q");
const StyleUtil = require("../StyleUtil");

var Conjurer = require("../Conjurer");

/**
 * @event ObjectControlAdapter#ValueChanged
 * @type {Object}
 * @property oldValue {string} The previous value
 * @property newValue {string} The new value
 */

/**
 * @class ObjectControlAdapter
 * @classdesc renders an Object Jtype.
 * @implements ControlAdapterInterface
 *
 * @param $scope {$scope} The $scope object attached to the containingElement
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires util
 * @requires EventEmitter
 *
 * @fires ObjectControlAdapter#ValueChanged
 */
module.exports = function ObjectAdapter(logger, containingElem, formCtrlDef) {
    var self = this;
    var loadDeferred = q.defer();
    var _scope;
    //call Event Emitter Constructor
    EventEmitter.call(this);

    //The name of the control
    this.name = formCtrlDef.name;
    this._accordionDisplay = _.get(formCtrlDef, "configSettings.Display.accordion", false);

    /**
     * @returns the data associated with this control.
     */
    this.getValue = function () {
        var val = formCtrlDef.value;
        //On initialization the child does not yet exist but thats ok as
        //the valueChanged events will handle this for us.
        if (_scope.ObjectRendererApi && _scope.ObjectRendererApi.getValue) {
            val = _scope.ObjectRendererApi.getValue();
        }
        return val;
    };

    /**
     * Set this controls current value.
     * @param val {string} The value to set this control to.
     * @returns the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    this.setValue = function (val, silent = true) {
        _scope.ObjectRendererApi.renderValues(val, silent);
    };

    this.getMultiEditActive = function () {
        return _scope.ObjectRendererApi.getMultiEditActive();
    };

    this.isInvalid = function () {
        return _scope.ObjectRendererApi.isInvalid();
    };

    this.isInProgress = function () {
        return _scope.ObjectRendererApi.isInProgress();
    };

    this.getValidationMessages = function () {
        return _scope.ObjectRendererApi.getValidationMessages();
    };

    this.toggleAccordion = () => {
        let collapsed = containingElem[0].classList.contains("collapsed");
        if (collapsed) {
            containingElem[0].classList.remove("collapsed");
        } else {
            containingElem[0].classList.add("collapsed");
        }
    };

    /**
     * Get any paths that "Could" be selected, but currently are not.
     * @param path Dotted notation of current object depth.
     * @returns [*]
     */
    this.getNonSelectedPaths = function (path) {
        return _scope.ObjectRendererApi.getNonSelectedPaths(path);
    };

    /**
     * Tear down this control
     */
    this.tearDown = function () {
        _scope.ObjectRendererApi.removeAllListeners();
        _scope.ObjectRendererApi.tearDown();
        if (this.accordionIcon) {
            this.accordionIcon.removeEventListener("click", this.toggleAccordion);
        }
    };

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angularis before this hook is called.
     * @param $scope
     */
    this.$init = function ($scope) {
        _scope = $scope;

        $scope.ObjectRendererSettings = {};

        //Setup directive
        this._fieldsetElem = $('<div class="nested_rendered_controls object_control"></div>');

        //Apply element styles defined in Display.styles
        let stylesConfig = _.get(formCtrlDef, "configSettings.Display.styles", {});
        StyleUtil.renderStylesConfig(stylesConfig, containingElem[0]);

        // Apply container layout styles - considering this element as parent container, it defines how its child elements are placed
        let layoutCssClasses = _.get($scope, "jTypeContext.layout.container.cssClasses", []);
        let objectRendererElem = $(
            '<gl-form-renderer data-api="ObjectRendererApi" data-settings="ObjectRendererSettings" class="' +
                layoutCssClasses.join(" ") +
                '"></gl-form-renderer>'
        );
        this._fieldsetElem.append(objectRendererElem);

        // Apply item layout styles - these styles customize how this element is placed inside parent element
        let itemLayoutCssClass = _.get($scope, "jTypeContext.layout.item.cssClass", "");
        containingElem.addClass(itemLayoutCssClass);

        if (this._accordionDisplay) {
            let legend = containingElem.find("label").first();
            this.accordionIcon = document.createElement("actionable-icon-gl");
            this.accordionIcon.classList.add("accordion_icon", "expanded");
            this.accordionIcon.dataset.dark = true;
            this.accordionIcon.dataset.icon = "form.expand_collapse";
            this.accordionIcon.addEventListener(
                "click",
                (this.toggleAccordion = this.toggleAccordion.bind(this))
            );
            legend.prepend(this.accordionIcon);
            if (_.get(self._accordionDisplay, "default") === "collapsed") {
                this.toggleAccordion();
            }
        }

        containingElem.append(this._fieldsetElem);

        //Notify the world this adapter is ready to be compiled/linked and values retrieved
        self.emit("InitializationComplete", {
            initializedValue: self.getValue(),
        });
        return loadDeferred.promise;
    };
    /**
     * @type {Array.<String>}
     * @description $inject Annotation By adding the $inject property onto the init function the Angular
     * injection parameters can be specified.
     */
    this.$init.$inject = ["$scope"];

    /**
     * A Hook for when this control has been compiled/Linked.
     * @param $scope {$scope} The Angular Scope variable
     *
     * if $compiledAndLinked.$inject is specified those injection parameters will be used as the arguments
     */
    this.$compiledAndLinked = function ($scope) {
        $scope.ObjectRendererApi.on("ValueChanged", function (evt) {
            self.emit("ValueChanged", evt);
        });
        $scope.ObjectRendererApi.on("MultiEditActive", function (evt) {
            self.emit("MultiEditActive", evt);
        });
        $scope.ObjectRendererApi.once("AllControlsLoaded", function (evt) {
            loadDeferred.resolve();
        });
        $scope.ObjectRendererApi.on("Invalid", function (evt) {
            self.emit("Invalid", evt);
        });
        $scope.ObjectRendererApi.on("InProgress", function (evt) {
            self.emit("InProgress", evt);
        });
        //Render the object
        $scope.ObjectRendererApi.render(formCtrlDef.configSettings.value, {
            configurationData: formCtrlDef.configData,
            refInstances: formCtrlDef.refInstances,
            refTypes: formCtrlDef.refTypes,
            displayModeOptions: formCtrlDef.displayModeOptions,
        });
    };

    this.setReadOnly = function (flag) {
        return _scope.ObjectRendererApi.setReadOnly(flag);
    };
};
//Make this API inherit from EventEmitter
util.inherits(module.exports, EventEmitter);

//Tell the Form renderer that this is an Angular Control.
module.exports.isAngularControl = true;
